import React, { useState, useEffect } from 'react';
import { 
  X, CheckCircle2, Clock, Truck, MapPin, Package, 
  ExternalLink, Loader2, AlertCircle, Calendar
} from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function TrackingModal({ isOpen, onClose, docketId, docketNumber }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && docketId) {
      fetchTracking();
    } else {
      setData(null);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docketId]);

  const fetchTracking = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API}/api/client-portal/dockets/${docketId}/track`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        throw new Error('Failed to load tracking details');
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || 'Error loading tracking milestones');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden animate-scaleIn font-ibm">
        {/* Header */}
        <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600/30 border border-blue-500/30 rounded-xl flex items-center justify-center text-blue-400">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 block">
                Live Shipment Milestone
              </span>
              <h2 className="text-lg font-bold font-manrope">
                #{docketNumber || data?.docket_number || 'Docket'}
              </h2>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-400 hover:text-white hover:bg-white/10 rounded-xl h-8 w-8 p-0"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content Body */}
        <div className="p-6 max-h-[75vh] overflow-y-auto">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-500 gap-2">
              <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
              <span className="text-xs">Fetching live shipment events...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-start gap-3 text-xs">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          ) : (
            <div>
              {/* Current Status Pill */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-6 flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase font-bold tracking-wider text-blue-600 block">
                    Current Milestone Status
                  </span>
                  <span className="text-sm font-bold text-slate-900 font-manrope">
                    {data?.current_status?.replace(/_/g, ' ') || 'In Transit'}
                  </span>
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-600 text-white shadow-xs">
                  Active
                </span>
              </div>

              {/* Stepper Timeline */}
              <div className="relative pl-6 space-y-6 before:content-[''] before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                {data?.steps?.map((step, idx) => {
                  const isDone = step.completed;
                  return (
                    <div key={idx} className="relative group">
                      {/* Step Circle Indicator */}
                      <div
                        className={`absolute -left-6 top-0.5 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          isDone
                            ? 'bg-emerald-600 text-white shadow-sm ring-4 ring-emerald-50'
                            : 'bg-white border-2 border-slate-300 text-slate-300'
                        }`}
                      >
                        {isDone ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-300"></div>
                        )}
                      </div>

                      {/* Step Content */}
                      <div className="pl-3">
                        <div className="flex items-center justify-between">
                          <h4 className={`text-sm font-bold ${isDone ? 'text-slate-900 font-manrope' : 'text-slate-400 font-manrope'}`}>
                            {step.title}
                          </h4>
                          {step.timestamp && (
                            <span className="text-[11px] text-slate-400 flex items-center gap-1 font-mono">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {step.timestamp}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 leading-relaxed ${isDone ? 'text-slate-600' : 'text-slate-400'}`}>
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-100 p-4 flex items-center justify-end">
          <Button
            onClick={onClose}
            className="bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs h-9 px-4"
          >
            Close Tracker
          </Button>
        </div>
      </div>
    </div>
  );
}
